import axios, { AxiosProgressEvent } from 'axios';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import {
  getFileCategory,
  getFileType,
  getMimeType
} from '@utils/media-library-utils.ts';
import { createMedia, getUploadFileUrl } from '@api/file-upload-api.ts';
import { uploadByUrl } from '@api/media-library-api.ts';

const { showSnackbar } = useSnackbarStore.getState();

export const uploadFileSlice = (set: any, get: any) => ({
  uploadingFiles: [] as any[],

  uploadFileOrFiles: async ({
    data,
    handleClose
  }: {
    data: File[];
    handleClose?: () => void;
  }) => {
    const folderId = useMediaLibraryStore.getState().currentFolder?.id;
    const folderTitle = useMediaLibraryStore.getState().currentFolder?.title;

    const localFiles = data.map((file) => ({
      _id: `local-${Math.random().toString(36).substring(2, 9)}`,
      name: file.name,
      folder: folderId || null,
      size: Math.round(file.size / 1024),
      path: URL.createObjectURL(file),
      mimetype: file.type,
      type: getFileType({ mimetype: file.type }),
      progress: 0,
      status: 'uploading',
      isLocal: true,
      startTime: Date.now(),
      loaded: 0,
      total: file.size
    }));

    set({ uploadingFiles: [...get().uploadingFiles, ...localFiles] });
    handleClose?.();

    const results: { success: boolean; name: string }[] = [];

    const uploadSingleFile = async (file: File) => {
      const mimetype = file.type || getMimeType(file.name);

      try {
        const { key, url } = await getUploadFileUrl({
          filename: file.name
        });
        if (!url || !key) throw new Error('Upload URL or Key missing');

        await axios.put(url, file, {
          headers: { 'Content-Type': mimetype },
          onUploadProgress: (event: AxiosProgressEvent) => {
            if (!event.total) return;
            const progress = Math.round((event.loaded * 100) / event.total);

            set((state: any) => ({
              uploadingFiles: state.uploadingFiles.map((f: any) =>
                f.name === file.name
                  ? {
                      ...f,
                      progress,
                      loaded: event.loaded,
                      total: event.total,
                      status: progress < 100 ? 'uploading' : 'processing'
                    }
                  : f
              )
            }));
          }
        });

        await createMedia({
          filename: file.name,
          path: key,
          info: {
            mimetype,
            type: getFileCategory(mimetype),
            folder: folderId || 'root',
            resource: 'release',
            isPrivate: false,
            size: file.size
          }
        });

        set((state: any) => ({
          uploadingFiles: state.uploadingFiles.filter(
            (f: any) => f.name !== file.name
          )
        }));

        results.push({ success: true, name: file.name });
      } catch (error: any) {
        set((state: any) => ({
          uploadingFiles: state.uploadingFiles.map((f: any) =>
            f.name === file.name ? { ...f, status: 'error', progress: 0 } : f
          )
        }));

        showSnackbar(
          axios.isAxiosError(error)
            ? error.response?.data?.message || `upload_error_${file.name}`
            : `upload_error_${file.name}`,
          SnackbarType.Error
        );

        results.push({ success: false, name: file.name });
      }
    };

    await Promise.allSettled(data.map(uploadSingleFile));

    const hasSuccess = results.some((r) => r.success);
    if (hasSuccess && folderId) {
      await Promise.all([
        get().getMediaLibraryFolders(folderId, folderTitle),
        get().getMedia(folderId)
      ]);
      showSnackbar('snackbar_success_media_text', SnackbarType.Success);
    }

    set((state: any) => ({
      uploadingFiles: state.uploadingFiles.filter(
        (f: any) => f.status === 'uploading' || f.status === 'processing'
      )
    }));
  },

  setSelectedFileFromUrl: async (url: string, handleClose: any) => {
    set({ uploadByUrlCallState: CallStateEnum.Loading });
    try {
      const blob = await uploadByUrl({ url });
      set({ uploadByUrlCallState: CallStateEnum.Loaded });
      handleClose?.();

      if (!blob) {
        showSnackbar('upload_file_error', SnackbarType.Error);
        return;
      }

      const fileName = url.split('/').pop() || 'downloaded-file';
      const file = new File([blob], fileName, { type: blob.type });

      await get().uploadFileOrFiles({ data: [file] });
    } catch {
      set({ uploadByUrlCallState: CallStateEnum.Error });
      showSnackbar('upload_file_error', SnackbarType.Error);
    }
  }
});
